import { isAnswerPatch, hasAnswerObservation, checklistAnswerMatchesRow } from '@/services/checklist/checklistObservations';
import nacl from 'tweetnacl';
import naclUtil from 'tweetnacl-util';

import type {
  ChecklistValueV2LocalRow,
  ChecklistValueV2Outcome,
  ChecklistValueV2RpcInput,
  ChecklistValueV2RpcResult,
} from '../../types/syncRecovery';

export interface ChecklistSnapshotFieldReference {
  id: string;
  originalFieldId: string | null;
  tipo?: unknown;
}

export interface ChecklistValuesV2Gateway {
  upsert(input: {
    checklistId: string;
    values: ChecklistValueV2RpcInput[];
    clientBuild: number;
  }): Promise<ChecklistValueV2RpcResult[]>;
}

export interface ChecklistValuesV2PreparedStore {
  get(operationId: string, fieldId: string): {
    fingerprint: string;
    payload: ChecklistValueV2RpcInput;
    dispatched?: boolean;
  } | null;
  set(operationId: string, fieldId: string, value: {
    fingerprint: string;
    payload: ChecklistValueV2RpcInput;
    dispatched?: boolean;
  }): void;
  delete(operationId: string, fieldId: string): void;
}

export interface ProcessChecklistValuesV2Input {
  operationId: string;
  operationTimestamp: number;
  checklistId: string;
  companyId?: string;
  answers: Record<string, unknown>;
  currentFieldIds: ReadonlySet<string>;
  snapshotFields: ReadonlyArray<ChecklistSnapshotFieldReference>;
  localValues: ReadonlyArray<ChecklistValueV2LocalRow>;
  remoteValues: ReadonlyArray<ChecklistValueV2LocalRow>;
  /** Lock da versão remota vista antes de preparar cada mídia local. */
  expectedRemoteUpdatedAtByField?: Readonly<Record<string, string | null>>;
  /** Campos que só podem ser criados; não atualizar remoto existente sem autorização anterior. */
  createOnlyFieldIds?: ReadonlySet<string>;
  clientBuild: number;
}

export interface ProcessChecklistValuesV2Result {
  outcomes: ChecklistValueV2Outcome[];
  terminalFieldIds: string[];
  activeFieldIds: string[];
}

const encodeUtf8 = (value: string): Uint8Array => naclUtil.decodeUTF8(value);

const CHECKLIST_VALUE_V2_PAYLOAD_VERSION = 2;

const sha512Hex = (value: string): string => Array.from(nacl.hash(encodeUtf8(value)))
  .map(byte => byte.toString(16).padStart(2, '0'))
  .join('');

const deterministicValueId = (value: string): string => {
  const hex = sha512Hex(value).slice(0, 32).split('');
  hex[12] = '5';
  hex[16] = ((parseInt(hex[16], 16) & 0x3) | 0x8).toString(16);
  const normalized = hex.join('');
  return [
    normalized.slice(0, 8),
    normalized.slice(8, 12),
    normalized.slice(12, 16),
    normalized.slice(16, 20),
    normalized.slice(20),
  ].join('-');
};

const stablePayload = (payload: ChecklistValueV2RpcInput): string => JSON.stringify({
  id: payload.id,
  campo_id: payload.campo_id,
  snapshot_field_id: payload.snapshot_field_id,
  valor: payload.valor,
  ...(payload.observacao !== undefined ? { observacao: payload.observacao } : {}),
  expected_updated_at: payload.expected_updated_at,
  ...(payload.expected_partner_revision !== undefined ? { expected_partner_revision: payload.expected_partner_revision } : {}),
  idempotency_key: payload.idempotency_key,
});

const toIsoTimestamp = (value: string | number | null | undefined): string | null => {
  if (value === null || value === undefined || value === '') return null;
  const normalized = typeof value === 'string' ? value.trim() : value;
  const date = new Date(normalized);
  if (Number.isNaN(date.getTime())) return null;
  return typeof normalized === 'string' ? normalized : date.toISOString();
};

const indexRows = (
  rows: ReadonlyArray<ChecklistValueV2LocalRow>,
  snapshotById: ReadonlyMap<string, ChecklistSnapshotFieldReference>
) => {
  const byCurrent = new Map<string, ChecklistValueV2LocalRow>();
  const bySnapshot = new Map<string, ChecklistValueV2LocalRow>();
  rows.forEach(row => {
    // O campo físico pode continuar corrente após receber um vínculo histórico.
    // Só reutiliza a identidade quando o snapshot confirma o mesmo campo original.
    if (row.campo_id && (!row.snapshot_field_id ||
      snapshotById.get(row.snapshot_field_id)?.originalFieldId === row.campo_id)) {
      byCurrent.set(row.campo_id, row);
    }
    if (row.snapshot_field_id) bySnapshot.set(row.snapshot_field_id, row);
  });
  return { byCurrent, bySnapshot };
};

const retryableOutcome = (
  fieldId: string,
  payload: ChecklistValueV2RpcInput,
  reason: string
): ChecklistValueV2Outcome => ({
  fieldId,
  valueId: payload.id,
  idempotencyKey: payload.idempotency_key,
  payload,
  payloadSha512: sha512Hex(stablePayload(payload)),
  accountingState: 'unaccounted',
  reason,
  terminal: false,
});

export class ChecklistValuesV2Service {
  constructor(
    private readonly gateway: ChecklistValuesV2Gateway,
    private readonly preparedStore?: ChecklistValuesV2PreparedStore
  ) {}

  clearPrepared(operationId: string, fieldIds: ReadonlyArray<string>, terminalPayloads?: ReadonlyArray<ChecklistValueV2RpcInput>): void {
    fieldIds.forEach(fieldId => {
      if (terminalPayloads) {
        const prepared = this.preparedStore?.get(operationId, fieldId);
        if (!prepared || !terminalPayloads.some(payload => payload.idempotency_key === prepared.payload.idempotency_key &&
            stablePayload(payload) === stablePayload(prepared.payload))) return;
      }
      this.preparedStore?.delete(operationId, fieldId);
    });
  }

  async process(input: ProcessChecklistValuesV2Input): Promise<ProcessChecklistValuesV2Result> {
    const snapshotByOriginal = new Map<string, ChecklistSnapshotFieldReference>();
    const snapshotById = new Map<string, ChecklistSnapshotFieldReference>();
    const ambiguousOriginalIds = new Set<string>();
    input.snapshotFields.forEach(field => {
      snapshotById.set(field.id, field);
      if (field.originalFieldId) {
        const existing = snapshotByOriginal.get(field.originalFieldId);
        if (existing && existing.id !== field.id) ambiguousOriginalIds.add(field.originalFieldId);
        else snapshotByOriginal.set(field.originalFieldId, field);
      }
    });
    const local = indexRows(input.localValues, snapshotById);
    const remote = indexRows(input.remoteValues, snapshotById);
    const currentPayloads: Array<{ fieldId: string; payload: ChecklistValueV2RpcInput }> = [];
    const historicalPayloads: Array<{ fieldId: string; payload: ChecklistValueV2RpcInput }> = [];
    const outcomes: ChecklistValueV2Outcome[] = [];

    Object.entries(input.answers).forEach(([fieldId, value]) => {
      const isCurrent = input.currentFieldIds.has(fieldId);
      const snapshotField = isCurrent
        ? null
        : snapshotById.get(fieldId)
          ?? (ambiguousOriginalIds.has(fieldId) ? null : snapshotByOriginal.get(fieldId))
          ?? null;
      const referenceType = isCurrent ? 'current' : snapshotField ? 'historical' : 'unknown';
      const createOnly = input.createOnlyFieldIds?.has(fieldId) === true;
      const matchingRemote = referenceType === 'historical'
        ? remote.bySnapshot.get(snapshotField!.id)
        : remote.byCurrent.get(fieldId);
      const matchingLocal = referenceType === 'historical'
        ? local.bySnapshot.get(snapshotField!.id) ?? local.byCurrent.get(fieldId)
        : local.byCurrent.get(fieldId);
      const noteOnly = isAnswerPatch(value) && !Object.prototype.hasOwnProperty.call(value, 'valor');
      const hasMediaBaseline = Object.prototype.hasOwnProperty.call(
        input.expectedRemoteUpdatedAtByField ?? {}, fieldId
      );
      const effectiveValue = noteOnly
        ? (hasMediaBaseline || !matchingRemote ? matchingLocal?.valor : matchingRemote.valor)
        : isAnswerPatch(value) ? value.valor : value;
      const valueId = (createOnly ? matchingLocal?.id : matchingRemote?.id)
        ?? matchingLocal?.id
        ?? deterministicValueId(`${input.operationId}:${fieldId}`);
      const expectedUpdatedAt = hasMediaBaseline
        ? input.expectedRemoteUpdatedAtByField![fieldId]
        : toIsoTimestamp(matchingRemote?.updated_at);
      const fingerprint = sha512Hex(JSON.stringify({
        payloadVersion: CHECKLIST_VALUE_V2_PAYLOAD_VERSION,
        operationId: input.operationId,
        operationTimestamp: input.operationTimestamp,
        fieldId,
        value,
        // A retomada da mesma nota conserva payload/key/CAS preparados. Não
        // renovar a autorização porque outra escolha apareceu depois do envio.
        ...(noteOnly ? { observationOnlyContract: 1 } : {}),
        referenceType,
        ...(hasMediaBaseline ? { mediaExpectedUpdatedAt: expectedUpdatedAt } : {}),
        ...(createOnly ? { createOnly: true } : {}),
      }));
      const prepared = this.preparedStore?.get(input.operationId, fieldId);
      const sentMediaAttempt = prepared?.dispatched === true && hasMediaBaseline;
      const payload: ChecklistValueV2RpcInput = sentMediaAttempt || prepared?.fingerprint === fingerprint
        ? prepared.payload
        : {
            id: valueId,
            campo_id: referenceType === 'historical' ? null : fieldId,
            snapshot_field_id: referenceType === 'historical' ? snapshotField!.id : null,
            valor: effectiveValue,
            ...(hasAnswerObservation(value) ? { observacao: value.observacao } : {}),
            expected_updated_at: createOnly ? null : expectedUpdatedAt,
            ...(isAnswerPatch(value) && value.expected_partner_revision !== undefined ? { expected_partner_revision: value.expected_partner_revision } : {}),
            idempotency_key: `syncv2:${input.operationId}:${fingerprint.slice(0, 48)}`.slice(0, 128),
          };
      if ((isAnswerPatch(value) && effectiveValue === undefined) ||
        (hasAnswerObservation(value) && typeof value.observacao === 'string' && value.observacao.length > 10000)) {
        outcomes.push(retryableOutcome(fieldId, payload, 'answer_patch_invalid'));
        return;
      }
      if (noteOnly && matchingRemote && (!input.companyId || matchingRemote.empresa_id !== input.companyId ||
          matchingRemote.checklist_id !== input.checklistId)) {
        outcomes.push(retryableOutcome(fieldId, payload, 'note_remote_identity_not_verified'));
        return;
      }
      // Sem vínculo comprovado, não enviar nem persistir um lock incompleto.
      const physicalRemote = input.remoteValues.find(row => row.campo_id === fieldId);
      if (physicalRemote?.snapshot_field_id &&
        snapshotById.get(physicalRemote.snapshot_field_id)?.originalFieldId !== fieldId) {
        outcomes.push(retryableOutcome(fieldId, payload, 'field_reference_not_resolved_locally'));
        return;
      }
      if (createOnly && matchingRemote) {
        const remoteReferenceMatches = referenceType === 'historical'
          ? matchingRemote.snapshot_field_id === snapshotField!.id
          : matchingRemote.campo_id === fieldId;
        const remoteIdentityMatches = Boolean(input.companyId) &&
          matchingRemote.id === valueId &&
          matchingRemote.empresa_id === input.companyId &&
          matchingRemote.checklist_id === input.checklistId &&
          remoteReferenceMatches &&
          checklistAnswerMatchesRow(value, matchingRemote) &&
          JSON.stringify(matchingRemote.valor) === JSON.stringify(effectiveValue);
        if (!remoteIdentityMatches) {
          // O remote apareceu sem coincidência exata; não o adote como estado
          // autorizado. Somente a mesma idempotency_key pode confirmar aplicação.
          outcomes.push(retryableOutcome(fieldId, payload, 'replay_remote_value_exists'));
          return;
        }
        // Reenvia a mesma chave e expected_updated_at=null para o servidor
        // confirmar pelo ledger ou retornar conflito em inserção concorrente.
      }
      if (!sentMediaAttempt && prepared?.fingerprint !== fingerprint) {
        this.preparedStore?.set(input.operationId, fieldId, { fingerprint, payload });
      }

      if (referenceType === 'unknown') {
        outcomes.push(retryableOutcome(fieldId, payload, 'field_reference_not_resolved_locally'));
      } else if (referenceType === 'historical') {
        historicalPayloads.push({ fieldId, payload });
      } else {
        currentPayloads.push({ fieldId, payload });
      }
    });

    outcomes.push(...await this.processBatch(input, currentPayloads, 'applied'));
    outcomes.push(...await this.processBatch(input, historicalPayloads, 'historical'));

    return {
      outcomes,
      terminalFieldIds: outcomes.filter(item => item.terminal).map(item => item.fieldId),
      activeFieldIds: outcomes.filter(item => !item.terminal).map(item => item.fieldId),
    };
  }

  private async processBatch(
    input: ProcessChecklistValuesV2Input,
    batch: Array<{ fieldId: string; payload: ChecklistValueV2RpcInput }>,
    expectedSuccess: 'applied' | 'historical'
  ): Promise<ChecklistValueV2Outcome[]> {
    if (batch.length === 0) return [];

    let response: ChecklistValueV2RpcResult[];
    try {
      response = await this.gateway.upsert({
        checklistId: input.checklistId,
        values: batch.map(item => item.payload),
        clientBuild: input.clientBuild,
      });
    } catch {
      return batch.map(item => retryableOutcome(item.fieldId, item.payload, 'rpc_request_failed'));
    }

    const validIndexes = new Set<number>();
    const responseIsValid = response.length === batch.length && response.every(item => {
      if (!Number.isInteger(item.input_index) || item.input_index < 0 || item.input_index >= batch.length) {
        return false;
      }
      if (validIndexes.has(item.input_index)) return false;
      validIndexes.add(item.input_index);
      const expectedId = batch[item.input_index].payload.id;
      return item.value_id === null || item.value_id === undefined || item.value_id === expectedId;
    });
    if (!responseIsValid) {
      return batch.map(item => retryableOutcome(item.fieldId, item.payload, 'rpc_response_invalid'));
    }

    const byIndex = new Map(response.map(item => [item.input_index, item]));
    return batch.map((item, index) => {
      const rpcResult = byIndex.get(index);
      if (!rpcResult) {
        return retryableOutcome(item.fieldId, item.payload, 'rpc_result_missing');
      }

      const payloadSha512 = sha512Hex(stablePayload(item.payload));
      if (rpcResult.result === 'rejected') {
        return retryableOutcome(
          item.fieldId,
          item.payload,
          rpcResult.reason ?? 'rpc_rejected'
        );
      }

      if (rpcResult.result === 'conflict') {
        return {
          fieldId: item.fieldId,
          valueId: rpcResult.value_id ?? item.payload.id,
          idempotencyKey: item.payload.idempotency_key,
          payload: item.payload,
          payloadSha512,
          accountingState: 'conflict_materialized',
          reason: rpcResult.reason ?? 'server_conflict',
          terminal: true,
        };
      }

      if (rpcResult.result !== expectedSuccess) {
        return retryableOutcome(item.fieldId, item.payload, 'rpc_result_category_mismatch');
      }

      return {
        fieldId: item.fieldId,
        valueId: rpcResult.value_id ?? item.payload.id,
        idempotencyKey: item.payload.idempotency_key,
        payload: item.payload,
        payloadSha512,
        accountingState: rpcResult.result === 'historical'
          ? 'historical_materialized'
          : 'server_applied',
        reason: rpcResult.reason,
        terminal: true,
      };
    });
  }
}
